/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/**
 * Ported from @material-ui/core@4.11.3
 */

import * as React from 'react';
import { useState, useRef, useEffect, isValidElement, cloneElement, Children } from 'react';
import cx from 'classnames';
import { useEventCallback } from '../../hooks/useEventCallback';
import TabIndicator from './TabIndicator';
import styles from './Tabs.module.scss';

type Props<T> = {
  /**
   * The label for the Tabs as a string.
   */
  'aria-label'?: string;

  /**
   * An id or list of ids separated by a space that label the Tabs.
   */
  'aria-labelledby'?: string;

  /**
   * The content of the component.
   */
  children?: React.ReactNode;

  /**
   * @ignore
   */
  className?: string;

  /**
   * Callback fired when the value changes.
   *
   * @param {object} event The event source of the callback
   * @param {any} value We default to the index of the child (number)
   */
  onChange?: (event: React.SyntheticEvent<HTMLButtonElement>, activeTabIndex: T) => void;

  /**
   * The value of the currently selected `Tab`.
   * If you don't want any selected `Tab`, you can set this property to `false`.
   */
  value?: any;
  variant?: 'fullWidth' | 'standard';
  hideIndicator?: boolean;
  TabIndicatorProps?: any;
  classes?: {
    [key: string]: string;
  };
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => unknown;

  /**
   * Indicates that the flex should not be used for the tabs container.
   * Allows to manipulate with the custom alignement.
   */
  flex?: boolean;
};

function Tabs<IndexType>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children: childrenProp,
  className,
  onChange,
  value,
  variant = 'standard',
  TabIndicatorProps,
  classes = {},
  flex = true,
  hideIndicator = false,
  ...other
}: Props<IndexType>) {
  const fallbackTabIndexRef = useRef<number | null>(null);
  const start = 'left';
  const size = 'width';
  const [mounted, setMounted] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState<any>({});
  const valueToIndex = new Map();
  const tabsRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  const updateIndicatorState = useEventCallback(() => {
    const { tabsMeta, tabMeta, selectedTab } = getTabsMeta();
    let startValue = 0;

    // Scroll to center the selected tab, ensuring adjacent tabs are visible
    if (selectedTab && tabsRef.current && selectedTab instanceof HTMLElement) {
      const container = tabsRef.current;
      const containerWidth = container.clientWidth;
      const tabLeft = selectedTab.offsetLeft;
      const tabWidth = selectedTab.offsetWidth;

      // Calculate scroll position to center the tab, ensuring adjacent tabs are visible
      const targetScroll = tabLeft - containerWidth / 2 + tabWidth / 2;

      container.scroll?.({
        left: Math.max(0, targetScroll),
        top: 0,
        behavior: 'smooth',
      });
    }

    if (tabMeta && tabsMeta) {
      const correction = tabsMeta.scrollLeft;
      startValue = tabMeta.left - tabsMeta.left + correction;
    }

    const newIndicatorStyle = {
      [start]: startValue,
      // May be wrong until the font is loaded.
      [size]: tabMeta ? tabMeta[size] : 0,
    };

    if (isNaN(indicatorStyle[start]) || isNaN(indicatorStyle[size])) {
      setIndicatorStyle(newIndicatorStyle);
    } else {
      const dStart = Math.abs(indicatorStyle[start] - newIndicatorStyle[start]);
      const dSize = Math.abs(indicatorStyle[size] - newIndicatorStyle[size]);

      if (dStart >= 1 || dSize >= 1) {
        setIndicatorStyle(newIndicatorStyle);
      }
    }
  });

  const getTabsMeta = (): { tabsMeta: any; tabMeta: any; selectedTab?: Element } => {
    const tabsNode = tabsRef.current;
    let tabsMeta;

    if (tabsNode) {
      const rect = tabsNode.getBoundingClientRect(); // create a new object with ClientRect class props + scrollLeft

      tabsMeta = {
        clientWidth: tabsNode.clientWidth,
        scrollLeft: tabsNode.scrollLeft,
        scrollTop: tabsNode.scrollTop,
        scrollWidth: tabsNode.scrollWidth,
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      };
    }

    let tabMeta;
    let selectedTab;

    const children = tabListRef.current?.children;

    if (children && children.length > 0) {
      const tabIndex =
        value !== false ? valueToIndex.get(value) : (fallbackTabIndexRef.current ?? 0);

      const tab = children[tabIndex];

      if (process.env.NODE_ENV !== 'production') {
        if (value !== false && !tab) {
          // eslint-disable-next-line no-console
          console.error(
            [
              `The value provided to the Tabs component is invalid.`,
              `None of the Tabs' children match with \`${value ?? 'undefined'}\`.`,
              valueToIndex.keys
                ? `You can provide one of the following values: ${Array.from(
                    valueToIndex.keys(),
                  ).join(', ')}.`
                : null,
            ].join('\n'),
          );
        }
      }

      selectedTab = tab;
      tabMeta = tab?.getBoundingClientRect() ?? null;
    }

    return {
      tabsMeta,
      tabMeta,
      selectedTab,
    };
  };

  const indicator = hideIndicator ? null : (
    <TabIndicator
      className={styles.indicator}
      classes={classes}
      {...TabIndicatorProps}
      style={{ ...indicatorStyle, ...TabIndicatorProps?.style }}
    />
  );
  let childIndex = 0;
  const children = Children.map(childrenProp, (child) => {
    if (!isValidElement(child)) {
      return null;
    }
    const tabChild = child as React.ReactElement<any, any>;
    const childValue = tabChild.props.value === undefined ? childIndex : tabChild.props.value;
    valueToIndex.set(childValue, childIndex);
    if (tabChild.props.selected) {
      fallbackTabIndexRef.current = childIndex;
    }
    const selected = value !== false ? childValue === value : !!tabChild.props.selected;
    childIndex += 1;
    return cloneElement(tabChild, {
      fullWidth: variant === 'fullWidth',
      indicator: selected && !mounted && indicator,
      selected,
      onChange,
      value: childValue,
    });
  });
  useEffect(() => {
    updateIndicatorState();
  });

  const handleKeyDown = (event: any) => {
    const { target } = event;

    // Keyboard navigation assumes that [role="tab"] are siblings
    // though we might warn in the future about nested, interactive elements
    // as a a11y violation
    const role = target.getAttribute('role');

    if (role !== 'tab') {
      return;
    }

    let newFocusTarget = null;
    const previousItemKey = 'ArrowLeft';
    const nextItemKey = 'ArrowRight';

    switch (event.key) {
      case previousItemKey:
        newFocusTarget = target.previousElementSibling || tabListRef.current?.lastChild;
        break;

      case nextItemKey:
        newFocusTarget = target.nextElementSibling || tabListRef.current?.firstChild;
        break;

      case 'Home':
        newFocusTarget = tabListRef.current?.firstChild;
        break;

      case 'End':
        newFocusTarget = tabListRef.current?.lastChild;
        break;

      default:
        break;
    }

    if (newFocusTarget !== null) {
      newFocusTarget?.focus();
      event.preventDefault();
    }
  };

  return (
    <div className={cx(flex && classes.root, flex && styles.root, className)} {...other}>
      <div className={cx(styles.scroller, classes.scroller)} ref={tabsRef}>
        {/* The tablist isn't interactive but the tabs are */}
        {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
        <div
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className={cx(flex && classes.flexContainer, flex && styles.flexContainer)}
          onKeyDown={handleKeyDown}
          ref={tabListRef}
          role="tablist"
        >
          {children}
        </div>
        {mounted && indicator}
      </div>
    </div>
  );
}

export default Tabs;
